import React from 'react';
import { twMerge } from 'tailwind-merge';

type ZoneProps = React.HTMLAttributes<HTMLDivElement>;

/**
 * FilterBar — canonical filter/search/display toolbar composition (Spec 239).
 *
 * Owns the vertical rhythm between zones and the mobile-wrap rule, so pages stop
 * hand-rolling the toolbar (the 2026-06-25 Journal flush-pills + 2-row-Display bugs
 * came from hand-rolled `.journal-filters` CSS). Compose with the sub-zones:
 *
 *   <FilterBar>
 *     <FilterBar.Segments> …FilterPill… </FilterBar.Segments>
 *     <FilterBar.Search>   <Input …/>   </FilterBar.Search>
 *     <FilterBar.Display>  {viewToggle} <Field orientation="inline">…</Field> </FilterBar.Display>
 *   </FilterBar>
 *
 * All zones pass through arbitrary div props (data-testid, aria-*, etc.).
 */
function FilterBarRoot({ className = '', children, ...rest }: ZoneProps) {
  // gap-3 = the section-gap token between zones; mb-4 below the bar.
  return (
    <div className={twMerge('flex flex-col gap-3 mb-4', className)} {...rest}>
      {children}
    </div>
  );
}

/** Filter-pill / segmented row (All / Mine / Shared). Wraps gracefully. */
function Segments({ className = '', children, ...rest }: ZoneProps) {
  return (
    <div className={twMerge('flex flex-wrap items-center gap-2', className)} {...rest}>
      {children}
    </div>
  );
}

/** Full-width search row. */
function Search({ className = '', children, ...rest }: ZoneProps) {
  return (
    <div className={twMerge('w-full', className)} {...rest}>
      {children}
    </div>
  );
}

/**
 * Display row: view-toggle on the LEFT, page-size / display control RIGHT-justified
 * (`justify-between`) — CEO decision 2026-06-25 ("right-justify the display/dropdown,
 * cleaner"). Children stay on one row at every breakpoint (no 2-row wrap).
 */
function Display({ className = '', children, ...rest }: ZoneProps) {
  return (
    <div className={twMerge('flex items-center justify-between gap-2', className)} {...rest}>
      {children}
    </div>
  );
}

Segments.displayName = 'FilterBar.Segments';
Search.displayName = 'FilterBar.Search';
Display.displayName = 'FilterBar.Display';

export const FilterBar = Object.assign(FilterBarRoot, { Segments, Search, Display });
